import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Modal } from '../../components/ui/Modal';
import {
  Code, Plus, Edit, Trash2, Play, Pause, Save, RefreshCw, AlertTriangle, CheckCircle, XCircle, Activity, Clock, Zap, Settings, FileText, Eye, EyeOff, TestTube, BarChart3
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const CustomHooks = () => {
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedHook, setSelectedHook] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showDocumentationModal, setShowDocumentationModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    event: '',
    priority: 'medium',
    code: '',
    enabled: true,
    version: '1.0.0'
  });
  const [testResults, setTestResults] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEvent, setFilterEvent] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const fetchHooks = async () => {
      try {
        setLoading(true);
        const response = await MockDataService.getCustomHooks();
        if (response.success) {
          setHooks(response.data);
        } else {
          setError(response.message || "Failed to fetch custom hooks.");
        }
      } catch (err) {
        setError("Failed to fetch custom hooks.");
        console.error("Error fetching custom hooks:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchHooks();
  }, []);

  const handleCreateHook = async () => {
    try {
      const response = await MockDataService.createCustomHook(formData);
      if (response.success) {
        setHooks(prev => [...prev, response.data]);
        setShowCreateModal(false);
        setFormData({
          name: '',
          description: '',
          event: '',
          priority: 'medium',
          code: '',
          enabled: true,
          version: '1.0.0'
        });
        alert('Custom hook created successfully!');
      } else {
        alert('Failed to create custom hook');
      }
    } catch (err) {
      alert('Failed to create custom hook');
      console.error("Error creating hook:", err);
    }
  };

  const handleEditHook = async () => {
    try {
      const response = await MockDataService.updateCustomHook(selectedHook.id, formData);
      if (response.success) {
        setHooks(prev => prev.map(hook => 
          hook.id === selectedHook.id ? response.data : hook
        ));
        setShowEditModal(false);
        setSelectedHook(null);
        alert('Custom hook updated successfully!');
      } else {
        alert('Failed to update custom hook');
      }
    } catch (err) {
      alert('Failed to update custom hook');
      console.error("Error updating hook:", err);
    }
  };

  const handleDeleteHook = async (hookId) => {
    if (window.confirm('Are you sure you want to delete this custom hook?')) {
      try {
        const response = await MockDataService.deleteCustomHook(hookId);
        if (response.success) {
          setHooks(prev => prev.filter(hook => hook.id !== hookId));
          alert('Custom hook deleted successfully!');
        } else {
          alert('Failed to delete custom hook');
        }
      } catch (err) {
        alert('Failed to delete custom hook');
        console.error("Error deleting hook:", err);
      }
    }
  };

  const handleToggleHook = async (hookId) => {
    try {
      const hook = hooks.find(h => h.id === hookId);
      const response = await MockDataService.toggleCustomHook(hookId);
      if (response.success) {
        setHooks(prev => prev.map(hook => 
          hook.id === hookId ? { ...hook, enabled: !hook.enabled } : hook
        ));
        alert(`Hook ${hook.enabled ? 'disabled' : 'enabled'} successfully!`);
      } else {
        alert('Failed to toggle hook status');
      }
    } catch (err) {
      alert('Failed to toggle hook status');
      console.error("Error toggling hook:", err);
    }
  };

  const handleTestHook = async (hook) => {
    try {
      setSelectedHook(hook);
      setShowTestModal(true);
      setTestResults(null);
      
      const response = await MockDataService.testCustomHook(hook.id);
      if (response.success) {
        setTestResults(response.data);
      } else {
        setTestResults({ success: false, message: response.message });
      }
    } catch (err) {
      setTestResults({ success: false, message: 'Test failed' });
      console.error("Error testing hook:", err);
    }
  };

  const handleViewDocumentation = (hook) => {
    setSelectedHook(hook);
    setShowDocumentationModal(true);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'error': return 'destructive';
      case 'testing': return 'outline';
      default: return 'outline';
    }
  };

  const filteredHooks = hooks
    .filter(hook => {
      const matchesSearch = hook.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            hook.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesEvent = filterEvent === 'all' || hook.event === filterEvent;
      const matchesStatus = filterStatus === 'all' || 
                            (filterStatus === 'enabled' && hook.enabled) ||
                            (filterStatus === 'disabled' && !hook.enabled) ||
                            (filterStatus === 'error' && hook.status === 'error');
      return matchesSearch && matchesEvent && matchesStatus;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const availableEvents = [
    'ticket.created',
    'ticket.updated',
    'ticket.resolved',
    'user.created',
    'user.updated',
    'notification.sent',
    'workflow.triggered',
    'plugin.installed',
    'plugin.uninstalled'
  ];

  if (loading) return <div className="p-6 text-center">Loading Custom Hooks...</div>;
  if (error) return <div className="p-6 text-center text-red-500">Error: {error}</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Custom Hooks</h1>
          <p className="text-gray-600 dark:text-gray-400">Create and manage custom hooks for plugin integration.</p>
        </div>
        <div className="flex space-x-2">
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Hook
          </Button>
          <Button variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Hooks</CardTitle>
            <Code className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{hooks.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <CheckCircle className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{hooks.filter(h => h.enabled).length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Executions Today</CardTitle>
            <Activity className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {hooks.reduce((sum, h) => sum + (h.executionsToday || 0), 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <BarChart3 className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {hooks.length > 0 ? Math.round(hooks.reduce((sum, h) => sum + (h.successRate || 0), 0) / hooks.length) : 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-64">
              <Input
                type="text"
                placeholder="Search hooks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <select
              value={filterEvent}
              onChange={(e) => setFilterEvent(e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Events</option>
              {availableEvents.map(event => (
                <option key={event} value={event}>{event}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Status</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
              <option value="error">Error</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Hooks List */}
      <div className="space-y-4">
        {filteredHooks.map((hook) => (
          <Card key={hook.id}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Code className="h-5 w-5 text-blue-600" />
                    <h3 className="text-lg font-semibold">{hook.name}</h3>
                    <Badge variant={getPriorityColor(hook.priority)}>
                      {hook.priority}
                    </Badge>
                    <Badge variant={getStatusColor(hook.status)}>
                      {hook.status}
                    </Badge>
                    <Badge variant={hook.enabled ? 'success' : 'secondary'}>
                      {hook.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-3">{hook.description}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Zap className="h-4 w-4 mr-1" />
                      <span>{hook.event}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>v{hook.version}</span>
                    </div>
                    <div className="flex items-center">
                      <Activity className="h-4 w-4 mr-1" />
                      <span>{hook.executionsToday || 0} today</span>
                    </div>
                    <div className="flex items-center">
                      <BarChart3 className="h-4 w-4 mr-1" />
                      <span>{hook.successRate || 0}% success</span>
                    </div>
                  </div>
                  {hook.lastExecuted && (
                    <div className="mt-2 text-sm text-gray-500">
                      Last executed: {new Date(hook.lastExecuted).toLocaleString()}
                    </div>
                  )}
                </div>
                <div className="flex flex-col space-y-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedHook(hook);
                      setFormData(hook);
                      setShowEditModal(true);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestHook(hook)}
                  >
                    <TestTube className="h-4 w-4 mr-1" />
                    Test
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDocumentation(hook)}
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Docs
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleHook(hook.id)}
                  >
                    {hook.enabled ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
                    {hook.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteHook(hook.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Hook Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} className="max-w-4xl">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Create Custom Hook</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Hook Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter hook name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Event</label>
                <select
                  value={formData.event}
                  onChange={(e) => setFormData({...formData, event: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="">Select event</option>
                  {availableEvents.map(event => (
                    <option key={event} value={event}>{event}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Enter hook description"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Version</label>
                <Input
                  value={formData.version}
                  onChange={(e) => setFormData({...formData, version: e.target.value})}
                  placeholder="1.0.0"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Hook Code</label>
              <Textarea
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value})}
                placeholder="Enter JavaScript code for the hook"
                rows={10}
                className="font-mono text-sm"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled}
                onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
              />
              <label htmlFor="enabled" className="text-sm font-medium">Enable hook</label>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateHook}>
                <Plus className="h-4 w-4 mr-2" />
                Create Hook
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit Hook Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} className="max-w-4xl">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Edit Custom Hook</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Hook Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Event</label>
                <select
                  value={formData.event}
                  onChange={(e) => setFormData({...formData, event: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  {availableEvents.map(event => (
                    <option key={event} value={event}>{event}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Version</label>
                <Input
                  value={formData.version}
                  onChange={(e) => setFormData({...formData, version: e.target.value})}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Hook Code</label>
              <Textarea
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value})}
                rows={10}
                className="font-mono text-sm"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="editEnabled"
                checked={formData.enabled}
                onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
              />
              <label htmlFor="editEnabled" className="text-sm font-medium">Enable hook</label>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleEditHook}>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Test Results Modal */}
      <Modal isOpen={showTestModal} onClose={() => setShowTestModal(false)} className="max-w-lg">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Hook Test Results</h2>
          {testResults && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${testResults.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center space-x-2">
                  {testResults.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <span className={`font-medium ${testResults.success ? 'text-green-800' : 'text-red-800'}`}>
                    {testResults.success ? 'Test Passed' : 'Test Failed'}
                  </span>
                </div>
                <p className={`mt-2 ${testResults.success ? 'text-green-700' : 'text-red-700'}`}>
                  {testResults.message}
                </p>
                {testResults.details && (
                  <div className="mt-3">
                    <h4 className="font-medium text-sm">Details:</h4>
                    <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-auto">
                      {JSON.stringify(testResults.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setShowTestModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Documentation Modal */}
      <Modal isOpen={showDocumentationModal} onClose={() => setShowDocumentationModal(false)} className="max-w-2xl">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Hook Documentation</h2>
          {selectedHook && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">{selectedHook.name}</h3>
                <p className="text-gray-600 mb-4">{selectedHook.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong>Event:</strong> {selectedHook.event}
                </div>
                <div>
                  <strong>Priority:</strong> {selectedHook.priority}
                </div>
                <div>
                  <strong>Version:</strong> {selectedHook.version}
                </div>
                <div>
                  <strong>Status:</strong> {selectedHook.status}
                </div>
              </div>
              {selectedHook.documentation && (
                <div>
                  <h4 className="font-medium mb-2">Documentation:</h4>
                  <div className="bg-gray-50 p-4 rounded-md">
                    <pre className="text-sm whitespace-pre-wrap">{selectedHook.documentation}</pre>
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <Button onClick={() => setShowDocumentationModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CustomHooks;